/**
 * The applicant's key documents and the borrower date each one carries — one
 * definition shared by the Applicants-tab card and the applicant sidebar's
 * "Key dates & documents" section, so the two can't drift.
 *
 * Per entry:
 *   target     — `{ docGroup, docType }` a file is filed under when uploaded or
 *                assigned here (exact names from the document taxonomy).
 *   matchers   — docType substrings (lower-case) that count as this item's file.
 *   preferred  — optional: the matcher whose files are shown first.
 *   altTargets — optional: other `{ docGroup, docType }`s an upload here may be
 *                filed as (offered as suggested names).
 *   dateField  — the borrower field the date writes, or null (no date).
 *   dateMode   — "effective": the date starts a validity window from
 *                DOC_TYPE_EXPIRATION_DAYS (credit / background reports);
 *                "expiration": the date IS the expiration (photo ID);
 *                "plain": a date with no validity (credit authorization);
 *                "none": document only.
 *   dateLabel / dateHelp — what the date is, from the backend data model
 *                (2026-10-01 check):
 *     creditReportDate — the date the report was PULLED (extractor: "report
 *                        pull date"; reportEffectiveDate says the same);
 *     backgroundDate   — the date the background report was RUN;
 *     idExpirationDate — the expiration printed on the ID;
 *     creditAuthDate   — undefined beyond "date of credit authorization";
 *                        nothing fills it, it holds what is typed here.
 *   The loan application has no date field on the borrower model (the only
 *   `loanApplicationDate` write is a dead constructor's extra field), so it is
 *   document-only rather than inventing one.
 */
import { useMemo } from "react";

import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";

export const VERIFICATIONS = [
  {
    key: "creditAuth",
    label: "Credit Auth / Loan Application",
    target: { docGroup: "Borrower Documents", docType: "Credit Authorization" },
    // Either document satisfies it: our loan applications carry the
    // borrower's authorization to pull credit. A standalone credit
    // authorization is preferred when both are on file.
    matchers: ["credit auth", "loan application"],
    // Uploads here can be filed as either; the sidebar suggests both names.
    altTargets: [
      { docGroup: "Borrower Documents", docType: "Loan Application" },
    ],
    preferred: "credit auth",
    dateField: "creditAuthDate",
    dateMode: "plain",
    dateLabel: "Authorization date",
    dateHelp:
      "Date of the credit authorization — a signed credit authorization or a loan application, which also authorizes the credit pull. The backend does not define it further, so it holds whatever date is entered here.",
  },
  {
    key: "creditReport",
    label: "Credit report",
    target: { docGroup: "Borrower Reports", docType: "Credit Report" },
    matchers: ["credit report"],
    dateField: "creditReportDate",
    dateMode: "effective",
    dateLabel: "Pull date",
    dateHelp:
      "The date the credit report was pulled. The report's validity window runs from it.",
  },
  {
    key: "background",
    label: "Background check",
    target: { docGroup: "Borrower Reports", docType: "Background Report" },
    // Background reports only — an OFAC report is its own document type
    // ("Borrower OFAC Report") and is not the background check.
    matchers: ["background"],
    dateField: "backgroundDate",
    dateMode: "effective",
    dateLabel: "Run date",
    dateHelp:
      "The date the background report was run. The report's validity window runs from it.",
  },
  {
    key: "loanApplication",
    label: "Loan application",
    target: { docGroup: "Borrower Documents", docType: "Loan Application" },
    matchers: ["loan application"],
    dateField: null,
    dateMode: "none",
  },
  {
    key: "photoId",
    label: "Photo ID",
    target: { docGroup: "Borrower Documents", docType: "Photo Identification" },
    matchers: ["photo identification", "passport", "driver"],
    dateField: "idExpirationDate",
    dateMode: "expiration",
    dateLabel: "Expiration date",
    dateHelp: "The expiration date printed on the ID.",
  },
];

export const VERIFICATIONS_BY_KEY = Object.fromEntries(
  VERIFICATIONS.map((v) => [v.key, v]),
);

const matches = (doc, matchers) => {
  const docType = String(doc?.docType || "").toLowerCase();
  return matchers.some((m) => docType.includes(m));
};

/**
 * Per verification key: `files` — the applicant's associated, visible docs of
 * that type; `candidates` — every visible loan doc of that type.
 */
export const useVerificationDocs = (loan, borrowerId) =>
  useMemo(() => {
    const docs = (loan?.loanDocuments || []).filter((d) => !isDocHidden(d));
    const borrower = (loan?.borrowers || []).find((b) => b?._id === borrowerId);
    const linked = new Set(
      (borrower?.associations?.loanDocuments || []).map(String),
    );
    const mine = docs.filter((d) => linked.has(String(d?._id)));
    const out = {};
    // An item with a `preferred` type lists that type first, so it is the
    // file the box shows when more than one kind is on file.
    const rank = (v) => (d) =>
      v.preferred &&
      String(d?.docType || "")
        .toLowerCase()
        .includes(v.preferred)
        ? 0
        : 1;
    VERIFICATIONS.forEach((v) => {
      const order = (a, b) => rank(v)(a) - rank(v)(b);
      out[v.key] = {
        files: mine.filter((d) => matches(d, v.matchers)).sort(order),
        candidates: docs.filter((d) => matches(d, v.matchers)).sort(order),
      };
    });
    return out;
  }, [loan?.loanDocuments, loan?.borrowers, borrowerId]);
